/**
 * Person LinkedIn prefill (features.personPrefill).
 * Drafts stay in the React Query cache until Accept. Discard drops the draft
 * and does not write. Mirrors the pack helper in person-prefill-accept.ts.
 */

import type { QueryClient } from '@tanstack/react-query'

import { API_ORIGIN } from '@/constants'
import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'

export const PERSON_PREFILL_SCALAR_KEYS = [
  'first_name',
  'last_name',
  'headline',
  'bio',
  'city',
  'country',
  'website',
  'linkedin_url',
  'x_url',
] as const

export const PERSON_PREFILL_LIST_KEYS = [
  'add_skills',
  'add_experiences',
  'add_education',
  'add_certifications',
] as const

export type PersonPrefillScalarKey = (typeof PERSON_PREFILL_SCALAR_KEYS)[number]
export type PersonPrefillListKey = (typeof PERSON_PREFILL_LIST_KEYS)[number]

export const PERSON_PREFILL_DRAFT_QUERY_KEY = ['person-prefill', 'linkedin-draft'] as const

export const PERSON_PREFILL_LIST_LABELS: Record<PersonPrefillListKey, string> = {
  add_skills: 'Skills',
  add_experiences: 'Experience',
  add_education: 'Education',
  add_certifications: 'Certifications',
}

export type PersonPrefillDraftResponse = {
  status: string
  written: boolean
  linkedin_url: string
  draft: Record<string, unknown>
  note?: string
}

export type PersonReviewForm = Record<PersonPrefillScalarKey, string>

/** Saved profile scalars. A `ProfileData` object satisfies this without an index signature. */
export type PersonScalarSource = Partial<Record<PersonPrefillScalarKey, unknown>>

export type PersonPrefillWrite = {
  path: string
  body: unknown
}

const PROFILE_UPDATE_PATH = '/api/profiles/my'
const SKILLS_PATH = '/api/profiles/onboarding/skills'
const EXPERIENCES_PATH = '/api/profiles/my/experiences'
const EDUCATION_PATH = '/api/profiles/my/education'
const CERTIFICATIONS_PATH = '/api/profiles/my/certifications'

function isEmpty(value: unknown): boolean {
  if (value == null) return true
  return !String(value).trim()
}

function text(value: unknown): string {
  return String(value ?? '').trim()
}

function asRow(item: unknown): Record<string, unknown> | null {
  if (!item || typeof item !== 'object') return null
  return item as Record<string, unknown>
}

/** Draft scalars that fill empty current fields only — never overwrite. */
export function fillEmptyScalars(
  current: PersonScalarSource,
  draft: Record<string, unknown>,
): Partial<Record<PersonPrefillScalarKey, string>> {
  const out: Partial<Record<PersonPrefillScalarKey, string>> = {}
  for (const key of PERSON_PREFILL_SCALAR_KEYS) {
    if (!(key in draft)) continue
    const suggested = text(draft[key])
    if (!suggested) continue
    if (isEmpty(current[key])) out[key] = suggested
  }
  return out
}

/**
 * List sections the user opted to keep (Accept checklist).
 * Missing keep flags default to true. Explicit false drops the section.
 */
export function selectListOptIns(
  draft: Record<string, unknown>,
  keep?: Partial<Record<PersonPrefillListKey, boolean>>,
): Partial<Record<PersonPrefillListKey, unknown[]>> {
  const flags = keep ?? {}
  const out: Partial<Record<PersonPrefillListKey, unknown[]>> = {}
  for (const key of PERSON_PREFILL_LIST_KEYS) {
    if (!(key in draft)) continue
    if (flags[key] === false) continue
    const items = draft[key]
    if (Array.isArray(items) && items.length > 0) out[key] = items
  }
  return out
}

export function defaultListKeep(
  draft: Record<string, unknown>,
): Partial<Record<PersonPrefillListKey, boolean>> {
  const keep: Partial<Record<PersonPrefillListKey, boolean>> = {}
  for (const key of PERSON_PREFILL_LIST_KEYS) {
    const items = draft[key]
    if (Array.isArray(items) && items.length > 0) keep[key] = true
  }
  return keep
}

export function savedReviewForm(saved: PersonScalarSource): PersonReviewForm {
  return {
    first_name: text(saved.first_name),
    last_name: text(saved.last_name),
    headline: text(saved.headline),
    bio: text(saved.bio),
    city: text(saved.city),
    country: text(saved.country),
    website: text(saved.website),
    linkedin_url: text(saved.linkedin_url),
    x_url: text(saved.x_url),
  }
}

/** Review form with suggestions applied only where the saved profile is empty. */
export function prefillReviewForm(
  saved: PersonScalarSource,
  draft: Record<string, unknown>,
): { form: PersonReviewForm; suggestions: Partial<Record<PersonPrefillScalarKey, string>> } {
  const suggestions = fillEmptyScalars(saved, draft)
  const form = savedReviewForm(saved)
  for (const key of PERSON_PREFILL_SCALAR_KEYS) {
    const suggested = suggestions[key]
    if (suggested) form[key] = suggested
  }
  return { form, suggestions }
}

export function listSectionSummary(key: PersonPrefillListKey, items: unknown[]): string {
  if (key === 'add_skills') {
    return items.map((item) => text(item)).filter(Boolean).join(', ')
  }
  return items
    .map((item) => {
      const row = asRow(item)
      if (!row) return ''
      if (key === 'add_experiences') {
        return [text(row.title), text(row.company)].filter(Boolean).join(' at ')
      }
      if (key === 'add_education') {
        return [text(row.degree), text(row.institution || row.school)].filter(Boolean).join(', ')
      }
      return [text(row.name), text(row.issuer)].filter(Boolean).join(', ')
    })
    .filter(Boolean)
    .join('; ')
}

/** Accept writes empty scalars only, plus checked list sections. */
export function acceptWrites(
  saved: PersonScalarSource,
  form: PersonReviewForm,
  draft: Record<string, unknown>,
  keep?: Partial<Record<PersonPrefillListKey, boolean>>,
): PersonPrefillWrite[] {
  const writes: PersonPrefillWrite[] = []
  const scalars: Partial<Record<PersonPrefillScalarKey, string>> = {}
  for (const key of PERSON_PREFILL_SCALAR_KEYS) {
    if (!isEmpty(saved[key])) continue
    const value = text(form[key])
    if (value) scalars[key] = value
  }
  if (Object.keys(scalars).length > 0) {
    writes.push({ path: PROFILE_UPDATE_PATH, body: scalars })
  }

  const lists = selectListOptIns(draft, keep)
  const skills = (lists.add_skills ?? []).map((item) => text(item)).filter(Boolean)
  if (skills.length > 0) {
    writes.push({ path: SKILLS_PATH, body: { skills } })
  }

  for (const item of lists.add_experiences ?? []) {
    const row = asRow(item)
    if (!row) continue
    const title = text(row.title)
    const company = text(row.company)
    const startDate = text(row.start_date)
    if (!title || !company || !startDate) continue
    const body: Record<string, string> = { title, company, start_date: startDate }
    const endDate = text(row.end_date)
    const description = text(row.description)
    if (endDate) body.end_date = endDate
    if (description) body.description = description
    writes.push({ path: EXPERIENCES_PATH, body })
  }

  for (const item of lists.add_education ?? []) {
    const row = asRow(item)
    if (!row) continue
    const institution = text(row.institution || row.school)
    const degree = text(row.degree)
    if (!institution || !degree) continue
    const body: Record<string, unknown> = {
      institution,
      degree,
      related_skill_names: Array.isArray(row.related_skill_names) ? row.related_skill_names : [],
    }
    for (const field of ['field_of_study', 'start_date', 'end_date', 'description'] as const) {
      const value = text(row[field])
      if (value) body[field] = value
    }
    writes.push({ path: EDUCATION_PATH, body })
  }

  for (const item of lists.add_certifications ?? []) {
    const row = asRow(item)
    if (!row) continue
    const name = text(row.name)
    const issuer = text(row.issuer)
    if (!name || !issuer) continue
    const body: Record<string, unknown> = {
      name,
      issuer,
      related_skill_names: Array.isArray(row.related_skill_names) ? row.related_skill_names : [],
    }
    for (const field of [
      'issued_at',
      'expires_at',
      'credential_id',
      'credential_url',
      'description',
    ] as const) {
      const value = text(row[field])
      if (value) body[field] = value
    }
    writes.push({ path: CERTIFICATIONS_PATH, body })
  }

  return writes
}

/** Discard produces no profile writes. */
export function discardWrites(): PersonPrefillWrite[] {
  return []
}

export type ContinueBasicInfo = {
  first_name: string
  last_name: string
  headline: string | null
  bio: string | null
  city: string | null
  country: string | null
}

/**
 * Continue payload for onboarding basic-info.
 * With the flag on, values that are still unaccepted suggestions are omitted.
 * Website, LinkedIn, and X are never included — only Accept posts those.
 */
export function continueBasicInfoPayload(options: {
  prefillEnabled: boolean
  accepted: boolean
  saved: PersonScalarSource
  form: PersonReviewForm
  suggestions: Partial<Record<PersonPrefillScalarKey, string>>
}): ContinueBasicInfo {
  const { prefillEnabled, accepted, saved, form, suggestions } = options
  if (!prefillEnabled) {
    return {
      first_name: text(saved.first_name) || 'User',
      last_name: text(saved.last_name),
      headline: text(form.headline) || null,
      bio: text(form.bio) || null,
      city: text(form.city) || null,
      country: text(form.country) || null,
    }
  }

  const field = (key: 'first_name' | 'last_name' | 'headline' | 'bio' | 'city' | 'country'): string => {
    const current = text(form[key])
    if (accepted) return current
    const suggested = suggestions[key]
    if (suggested && current === suggested && isEmpty(saved[key])) return ''
    return current
  }

  return {
    first_name: field('first_name') || text(saved.first_name) || 'User',
    last_name: field('last_name'),
    headline: field('headline') || null,
    bio: field('bio') || null,
    city: field('city') || null,
    country: field('country') || null,
  }
}

export function storePersonPrefillDraft(
  queryClient: QueryClient,
  draft: PersonPrefillDraftResponse,
): void {
  queryClient.setQueryData(PERSON_PREFILL_DRAFT_QUERY_KEY, draft)
}

export function readPersonPrefillDraft(
  queryClient: QueryClient,
): PersonPrefillDraftResponse | undefined {
  return queryClient.getQueryData(PERSON_PREFILL_DRAFT_QUERY_KEY)
}

export function dropPersonPrefillDraft(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: PERSON_PREFILL_DRAFT_QUERY_KEY })
}

/** POST /api/profiles/prefill/linkedin. Does not update the profile. */
export async function draftPersonFromLinkedIn(
  linkedinUrl: string,
  signal?: AbortSignal,
): Promise<PersonPrefillDraftResponse> {
  const response = await authorizedFetch(`${API_ORIGIN}/api/profiles/prefill/linkedin`, {
    method: 'POST',
    body: JSON.stringify({ linkedin_url: linkedinUrl.trim() }),
    signal,
  })
  if (!response.ok) {
    throw new Error(
      await parseApiErrorMessage(response, "Couldn't draft from that LinkedIn link."),
    )
  }
  const body = (await response.json()) as PersonPrefillDraftResponse
  if (body.written !== false) {
    throw new Error('LinkedIn draft was refused because it would write the profile.')
  }
  return body
}

export async function postPersonPrefillAccept(writes: PersonPrefillWrite[]): Promise<void> {
  for (const write of writes) {
    const response = await authorizedFetch(`${API_ORIGIN}${write.path}`, {
      method: 'POST',
      body: JSON.stringify(write.body),
    })
    if (!response.ok) {
      throw new Error(await parseApiErrorMessage(response, 'Could not save the accepted fields.'))
    }
  }
}
